import React, { SetStateAction } from 'react';
import { FormControlLabel } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox, CustomAutocomplete,
  CustomSelect,
  DatePicker,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Progress, renderDropDownOption, renderDropDownTags,
  Typography,
} from 'web-library';
import ApiButton from 'web-library/stories/atom/Button/ApiButton';
import {configurationTypeData, dropDownData} from '../../../modules/admin/logic/dropDownOption';
import { useGetUniqueLinkDetails } from '../../../api-refactored/unique-link/useGetUniqueLinkDetails';
import { useCustomSelectDropdown } from '../../manageAnnouncements/CreateNewAnnouncement/useCreateEditAnnouncement';
import AdminSelectedSubjects from './AdminSelectedSubjects';
import { usePostInvalidateUniqueLink } from '../../../api-refactored/unique-link/usePostInvalidateUniqueLink';
import dayjs from 'dayjs';
import {DropDownItem} from "../../../hooks/dropdown";
import {ConfigurationType} from "web-library/models/DropDown";

const useStyles = makeStyles()((theme) =>
({
  selectContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    maxWidth: '960px',
  },
  cancelButton: {
    marginRight: theme.spacing(2),
  },
  rootContainer: {
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.spacing(6),
    padding: '0 25px 0 16px !important',
  },
  dialog: {
    overflow: 'hidden',
  },
  chipsContainer: {
    overflowX: 'hidden',
    width: '100%',
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  iconButton: {
    position: 'absolute',
    right: '45px',
    top: 'calc(50% - 12px)',
    padding: 0,
  },
  chipLabel: {
    padding: 0,
  },
  arrowIconContainer: {
    right: '-10px',
  },
}));

interface UniqueLinkDetailsModalContentProps {
  uniqueLinkToken?: string;
  setSelectedUniqueLinkToken: React.Dispatch<SetStateAction<string | undefined>>;
  refetchUniqueLinkListing: () => void;
  disableInvalidate: boolean;
}

const UniqueLinkDetailsModal = ({
  uniqueLinkToken,
  setSelectedUniqueLinkToken,
  refetchUniqueLinkListing,
  disableInvalidate,
}: UniqueLinkDetailsModalContentProps) => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const { data: uniqueLinkDetails, isLoading } = useGetUniqueLinkDetails({
    token: uniqueLinkToken,
    enabled: !!uniqueLinkToken,
  });

  const { mutate: invalidateUniqueLinkMutate, status: invalidateUniqueLinkApiStatus } =
    usePostInvalidateUniqueLink({
      onSuccess: () => {
        setSelectedUniqueLinkToken(undefined);
        refetchUniqueLinkListing();
      },
    });

  const { handleMenuItemsRender } = useCustomSelectDropdown({
    classes,
  });

  const handleCloseUniqueLinkDetailsModal = () => {
    setSelectedUniqueLinkToken(undefined);
  };

  const handleInvalidateUniqueLink = () => {
    if (uniqueLinkToken) {
      invalidateUniqueLinkMutate(uniqueLinkToken);
    }
  };

  if (isLoading) {
    return <Progress />;
  }
  const exemptionRulesDropDownData = dropDownData(uniqueLinkDetails?.exemptionRules);
  return (
    <Dialog
      id="unique-link-listing-modal-dialog"
      fullWidth
      open={!!uniqueLinkToken}
      maxWidth="lg"
      slotProps={{paper: {className: classes.dialog}}}
    >
      <DialogTitle id="unique-link-details-modal-title">
        {t('admin->>UniqueLink.UniqueLinkDetailsModalTitle')}
      </DialogTitle>
      <DialogContent id="unique-link-details-modal-content">
        <DatePicker
          label={t('admin->>UniqueLink.ExpiryDateLabel')}
          value={dayjs(uniqueLinkDetails?.expiryDate)}
          // className={classes.input}
          id="input-text-expiryDate"
          // @ts-ignore
          disabled
          onChange={() => { }}
          name="expiryDate"
          inputVariant="filled"
        />
        <Box className={classes.selectContainer}>
          <Typography>{t('admin->>UniqueLink.ViewDetailsAccessTypeGrantedLabel')}</Typography>

          <FormControlLabel
            control={<Checkbox />}
            checked={uniqueLinkDetails?.isWaiveSubjectAdminFee}
            disabled
            label={t('admin->>UniqueLink.WaiveSubjectAdminFees')}
          />

          <FormControlLabel
            control={<Checkbox />}
            checked={uniqueLinkDetails?.isWaiveLateFee}
            disabled
            label={t('admin->>UniqueLink.WaiveLateFees')}
          />
        </Box>

        {uniqueLinkDetails?.exemptionRules && uniqueLinkDetails.exemptionRules.length > 0 && (
          <Box className={classes.selectContainer}>
            <CustomAutocomplete<DropDownItem, true,false, undefined>
                id="inputSelect-rulesValidationsToBypass"
                label={t('admin->>UniqueLink.RulesValidationsToBypass')}
                value={exemptionRulesDropDownData}
                multiple
                disabled
                options={exemptionRulesDropDownData}
                isOptionEqualToValue={(option, v) => option.key === v.key}
                renderOption={renderDropDownOption}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.children)}
                renderTags={renderDropDownTags}
            />


          </Box>
        )}
        {uniqueLinkDetails?.subjects && uniqueLinkDetails.subjects.length > 0 && (
          <AdminSelectedSubjects
            adminSelectedSubjects={uniqueLinkDetails?.subjects.map((s) => ({
              ...s,
              optionalPapers: s.papers,
            }))}
            setAdminSelectedSubjects={() => { }}
            adminEditedSubjectDetails={{
              subjectUuid: '',
              subjectName: '',
              subjectCode: '',
              examSeries: '',
              examLevel: '',
              subjectLevel: '',
              language: '',
              actionRequired: false,
              allowedToAdd: false,
            }}
            setAdminEditedSubjectDetails={() => { }}
            adminDeletedSubjectDetails={{
              subjectName: '',
              subjectCode: '',
            }}
            setAdminDeletedSubjectDetails={() => { }}
            disabled
          />
        )}
      </DialogContent>
      <DialogActions>
        <Box className={classes.buttonContainer}>
          <Button
            id="button-close-unique-link-details-modal"
            type="button"
            className={classes.cancelButton}
            variant="text"
            onClick={handleCloseUniqueLinkDetailsModal}
          >
            {t('admin->>UniqueLink.Close')}
          </Button>
          <ApiButton
            status={invalidateUniqueLinkApiStatus}
            type="button"
            disabled={disableInvalidate}
            onClick={handleInvalidateUniqueLink}
            id="invalidate-unique-link-button"
          >
            {t('admin->>UniqueLink.Invalidate')}
          </ApiButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
export default UniqueLinkDetailsModal;
